import { z } from 'zod'
import type {
  MarketingDecision,
  Receipt,
  StoryDecision,
  Visit,
} from '../domain/story'

const uuid = z.uuid()
const instant = z.iso.datetime({ offset: true }).transform(Date.parse)
const version = z.literal(1)
const action = z.enum(['cta', 'close', 'completed'])
export const offersPageSchema = z.object({
  schema_version: version,
  items: z.array(
    z
      .object({ id: uuid, ends_at: instant })
      .transform((item) => ({ id: item.id, endsAt: item.ends_at })),
  ),
  has_more: z.boolean(),
})
export const visitSchema = z
  .object({
    schema_version: version,
    visit_id: uuid,
    timezone: z.string().min(1),
    day_ends_at: instant,
    recommendation_retry_after_seconds: z
      .number()
      .int()
      .min(60)
      .max(86400)
      .nullish(),
  })
  .transform((dto): Visit => ({
    id: dto.visit_id,
    timezone: dto.timezone,
    dayEndsAt: dto.day_ends_at,
    ...(dto.recommendation_retry_after_seconds != null
      ? {
          recommendationRetryAfterSeconds:
            dto.recommendation_retry_after_seconds,
        }
      : {}),
  }))

export const storySchema = z.object({
  schema_version: version,
  title: z.string().min(1).max(140),
  body: z.string().min(1).max(2000),
  gradient_start: z.string().regex(/^#[\da-f]{6}$/i),
  glass_first_color: z
    .string()
    .regex(/^#[\da-f]{6}$/i)
    .nullish(),
  glass_second_color: z
    .string()
    .regex(/^#[\da-f]{6}$/i)
    .nullish(),
  glass_background_color: z
    .string()
    .regex(/^#[\da-f]{6}$/i)
    .nullish(),
  gradient_end: z.string().regex(/^#[\da-f]{6}$/i),
  animation_speed: z.number().min(1).max(100).default(2),
  animation: z
    .enum(['gradient-v1', 'glass-v2', 'glass-v3'])
    .default('gradient-v1'),
  text_color: z
    .string()
    .regex(/^#[\da-f]{6}$/i)
    .default('#ffffff'),
  image_path: z.string().nullish(),
  cta_label: z.string().min(1).max(80),
  cta_path: z.string().min(1).max(2048),
})
export const decisionSchema = z
  .discriminatedUnion('kind', [
    z.object({ schema_version: version, kind: z.literal('none') }),
    z.object({
      schema_version: version,
      kind: z.literal('marketing'),
      reservation_id: uuid,
      campaign_id: uuid,
      revision_id: uuid,
      expires_at: instant,
      story: storySchema,
    }),
  ])
  .transform((dto): StoryDecision => {
    if (dto.kind === 'none') return { kind: 'none' }
    return {
      kind: 'marketing',
      reservationId: dto.reservation_id,
      campaignId: dto.campaign_id,
      revisionId: dto.revision_id,
      expiresAt: dto.expires_at,
      story: {
        title: dto.story.title,
        body: dto.story.body,
        gradientStart: dto.story.gradient_start,
        gradientEnd: dto.story.gradient_end,
        glassBackgroundColor: dto.story.glass_background_color,
        glassSecondColor: dto.story.glass_second_color,
        glassFirstColor: dto.story.glass_first_color,
        animationSpeed: dto.story.animation_speed,
        animation: dto.story.animation,
        textColor: dto.story.text_color,
        imagePath: dto.story.image_path ?? null,
        ctaLabel: dto.story.cta_label,
        ctaPath: dto.story.cta_path,
      },
    } satisfies MarketingDecision
  })
export const receiptSchema = z
  .object({
    schema_version: version,
    reservation_id: uuid,
    shown_at: instant,
    first_action: action.nullish(),
  })
  .transform((dto): Receipt => ({
    reservationId: dto.reservation_id,
    shownAt: dto.shown_at,
    firstAction: dto.first_action ?? null,
  }))

export function mapStory(dto: z.infer<typeof storySchema>) {
  return {
    title: dto.title,
    body: dto.body,
    gradientStart: dto.gradient_start,
    gradientEnd: dto.gradient_end,
    glassBackgroundColor: dto.glass_background_color,
    glassSecondColor: dto.glass_second_color,
    glassFirstColor: dto.glass_first_color,
    animationSpeed: dto.animation_speed,
    animation: dto.animation,
    textColor: dto.text_color,
    imagePath: dto.image_path ?? null,
    ctaLabel: dto.cta_label,
    ctaPath: dto.cta_path,
  }
}
